import { getDKAddress, } from '@debbie/common/dist/addresses/adressevaelger.js';
import { debounce } from '@debbie/common/dist/util/index.js';
import { useDKAddressSearch } from '@debbie/cortex/dist/addresses/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import React, { useId, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { errorNotification } from '../utils/errors.js';
import Errors from './Errors.js';
import Input from './Input.js';
const AddressFinder = (props) => {
    const lang = useSelector((s) => s.lang);
    const listId = useId();
    const [query, setQuery] = useState('');
    const [searchText, setSearchText] = useState('');
    const [narrowing, setNarrowing] = useState(null);
    const [highlighted, setHighlighted] = useState(0);
    const [isListClosed, setIsListClosed] = useState(false);
    const search = useDKAddressSearch(searchText, narrowing?.district ?? null);
    const visibleSuggestions = isListClosed || !query.trim() || !h.hasData(search) ? [] : search.data;
    const searchFor = (text) => {
        setSearchText(text);
        setHighlighted(0);
    };
    const select = async (suggestion) => {
        if (suggestion.addressId === null) {
            setQuery(suggestion.narrowing.text);
            setNarrowing(suggestion.narrowing);
            searchFor(suggestion.narrowing.text);
            return;
        }
        setQuery(suggestion.title);
        setIsListClosed(true);
        try {
            props.onSelect?.(await getDKAddress(suggestion.addressId));
        }
        catch (err) {
            errorNotification(lang, err, 'any', lang.inputs.searchAddress);
        }
    };
    const placeholder = props.placeholder ?? lang.inputs.searchAddress;
    const onKeyDown = (e) => {
        const count = visibleSuggestions.length;
        if (e.key === 'ArrowDown' && count > 0) {
            e.preventDefault();
            setHighlighted((highlighted + 1) % count);
        }
        else if (e.key === 'ArrowUp' && count > 0) {
            e.preventDefault();
            setHighlighted((highlighted - 1 + count) % count);
        }
        else if (e.key === 'Enter' && highlighted < count) {
            e.preventDefault();
            // Until the debounced search runs, the suggestions are for older text
            if (searchText === query) {
                void select(visibleSuggestions[highlighted]);
            }
        }
        else if (e.key === 'Escape') {
            setIsListClosed(true);
        }
        props.onKeyDown?.(e);
    };
    return (React.createElement(AddressFinderWrapper, { className: props.className },
        React.createElement(Input, { autofocus: props.autofocus, disabled: props.disabled, value: query, onChange: (e) => {
                const text = e.target.value;
                setQuery(text);
                setIsListClosed(false);
                // Keep searching within the chosen street's district until it's edited away
                if (narrowing !== null && !text.startsWith(narrowing.street)) {
                    setNarrowing(null);
                }
                debounce(listId, () => searchFor(text), 300);
            }, placeholder: placeholder, button: props.button, onBlur: props.onBlur, onKeyDown: onKeyDown, autoComplete: "off", 
            // The suggestions go directly under the input, so the wrapper keeps its margin
            style: { marginBottom: 0 }, aria: {
                role: 'combobox',
                'aria-label': placeholder,
                'aria-autocomplete': 'list',
                'aria-controls': listId,
                'aria-expanded': visibleSuggestions.length > 0,
                'aria-activedescendant': highlighted < visibleSuggestions.length
                    ? `${listId}-${highlighted}`
                    : undefined,
            } }),
        h.isError(search) && (React.createElement(Errors, { errors: [`${lang.misc.errorPerforming} ${placeholder}`] })),
        visibleSuggestions.length > 0 && (React.createElement(Suggestions, { id: listId, role: "listbox", "aria-label": placeholder }, visibleSuggestions.map((suggestion, index) => (React.createElement(Suggestion, { key: `${suggestion.addressId}-${suggestion.title}`, id: `${listId}-${index}`, role: "option", "aria-selected": index === highlighted, $highlighted: index === highlighted, 
            // Keep focus in the input so onBlur doesn't close the finder
            onMouseDown: (e) => e.preventDefault(), onClick: () => void select(suggestion) }, suggestion.title)))))));
};
export default AddressFinder;
const AddressFinderWrapper = styled.div `
  position: relative;
  width: 100%;
  margin-bottom: 20px;
`;
const Suggestions = styled.ul `
  margin: 0;
  padding: 0;
  text-align: left;
  border-radius: 0.3125em;
  background: #fcfcfc;
  box-shadow: 0 0.0625em 0.15625em rgba(0, 0, 0, 0.15);
  position: absolute;
  left: 0;
  right: 0;
  z-index: 9999;
  overflow-y: auto;
  max-height: 20em;
  box-sizing: border-box;
`;
const Suggestion = styled.li `
  margin: 0;
  list-style: none;
  cursor: pointer;
  padding: 0.4em 0.6em;
  color: #333;
  border: 0.0625em solid #ddd;
  border-bottom-width: 0;
  background: ${(p) => (p.$highlighted ? '#f0f0f0' : 'transparent')};

  &:first-child {
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
  }

  &:last-child {
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
    border-bottom-width: 0.0625em;
  }

  &:hover {
    background: #f0f0f0;
  }
`;
//# sourceMappingURL=AddressFinder.js.map